import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { State } from 'src/app/reducers';
import {
  clearCurrentUserAssessment,
  createUserAssessmentSuccess,
  submitUserAssessmentQuestion,
} from 'src/app/store/assessment/assessment.actions';
import {
  Question,
  UserAssessment,
} from 'src/app/store/assessment/assessment.models';
import { User } from 'src/app/store/auth/auth.models';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-questionaires',
  templateUrl: './questionaires.component.html',
  styleUrls: ['./questionaires.component.scss'],
})
export class QuestionairesComponent implements OnInit, OnDestroy {
  private destroyer$: Subject<any> = new Subject();
  @ViewChild('autosize') autosize: CdkTextareaAutosize;

  pageLoading = true;
  currentUser: User = null;
  errorMessage: string = null;
  activeAssessment: UserAssessment = null;
  currentQuestion: Question = null;
  totalNumberOfQuestions: number = 0;
  currentQuestionNumber: number = 0;
  userAnswer: BehaviorSubject<any> = new BehaviorSubject(null);
  changingQuestion: boolean = false;

  constructor(private store: Store<State>, private router: Router, private _ngZone: NgZone, private qService: AppService<any>) { }

  ngOnInit(): void {
    this.setupPage();
  }

  triggerResize(): void {
    this._ngZone.onStable
      .pipe(take(1))
      .subscribe(() => this.autosize.resizeToFitContent(true));
  }

  private startTest(t: UserAssessment): void {
    if (!t) {
      this.router.navigate(['/assessments']);
    }
    this.activeAssessment = t;
    this.totalNumberOfQuestions = t.questions.length;
    this.pageLoading = false;
    this.currentQuestion = t.questions[this.currentQuestionNumber];
    this.userAnswer.next(this.currentQuestion.user_answer || null);
  }

  private setupPage(): void {
    this.store
      .select((state) => state.assessments.currentUserAssessment)
      .pipe(takeUntil(this.destroyer$))
      .subscribe((t) => {
        if (!!t) {
          this.startTest(t);
        } else {
          this.startTest(t);
          this.store.dispatch(createUserAssessmentSuccess({ assessment: t }));
        }
      });
    this.store
      .select((state) => state.auth.authenticatedUser)
      .pipe(takeUntil(this.destroyer$))
      .subscribe((u) => (this.currentUser = u));
  }

  nextQuestion(): void {
    this.changingQuestion = true;
    if (this.userAnswer.value !== this.currentQuestion.user_answer) {
      this.store.dispatch(
        submitUserAssessmentQuestion({
          userId: this.currentUser.sub,
          question: this.currentQuestion.question,
          answer: this.userAnswer.value,
          assessmentId: this.activeAssessment.id,
        })
      );
    }
    this.currentQuestion = this.activeAssessment.questions[
      ++this.currentQuestionNumber
    ];
    this.userAnswer.next(this.currentQuestion.user_answer);
    this.changingQuestion = false;
  }

  previousQuestion(): void {
    if (this.currentQuestionNumber - 1 >= 0) {
      this.currentQuestion = this.activeAssessment.questions[
        --this.currentQuestionNumber
      ];
      this.userAnswer.next(this.currentQuestion.user_answer);
    }
  }

  onFreeAnswerChanged(data: any): void {
    this.userAnswer.next(data.target.value);
  }

  onOptionSelectedChanged(data: any): void {
    this.userAnswer.next(data.value);
  }

  submitAssessment(): void {
    const params = {
      question: this.currentQuestion.question,
      answer: this.userAnswer.value,
      assessment_id: this.activeAssessment.id,
      action: 'SUBMIT',
    };
    this.qService.put('assessments', this.currentUser.sub, {appId: environment.appId}, params)
      .then((res: UserAssessment) => {
        this.store.dispatch(clearCurrentUserAssessment());
        this.router.navigate(['/assessments/', res.id, 'review']);
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearCurrentUserAssessment());
    this.destroyer$.next(false);
    this.destroyer$.complete();
  }
}
